import { useSiteModulesApi } from "../api";
import {
  getAllModuleStaticDetails,
  Module,
  ModuleCategory,
} from "../constants/modules";
import { useCurrentProductionArea } from "./useCurrentProductionArea";

export type GroupedModules = Record<ModuleCategory, Module[]>;

export type UseModules = {
  isLoading: boolean;
  isError: boolean;
  modules?: GroupedModules;
};

export const useModules = (site: string, requestedProductionArea?: string) => {
  const { productionArea, isLoading: isProductionAreaLoading } =
    useCurrentProductionArea(site, requestedProductionArea);
  const { data: moduleDtos, isLoading, isError } = useSiteModulesApi(site);

  if (isLoading || isProductionAreaLoading) {
    return {
      isLoading: true,
      isError: false,
      modules: undefined,
    };
  }

  if (isError || !productionArea || !moduleDtos) {
    return {
      isLoading: false,
      isError: true,
      modules: undefined,
    };
  }

  const allModules = getAllModuleStaticDetails();
  const modules = mergeById(allModules, moduleDtos);

  return {
    isLoading: false,
    isError: false,
    modules: groupByCategory(modules),
  };
};

type WithId<Id extends PropertyKey = string> = { id: Id };

function mergeById<
  Id extends PropertyKey,
  A extends WithId<Id>,
  B extends WithId<Id>,
>(as: readonly A[], bs: readonly B[]) {
  const bById = new Map<Id, B>(bs.map((b) => [b.id, b]));
  const out: Array<A & B> = [];

  for (const a of as) {
    const b = bById.get(a.id);
    if (b) out.push({ ...a, ...b } as A & B);
  }
  return out;
}

function groupByCategory(items: readonly Module[]) {
  return items.reduce<Record<ModuleCategory, Module[]>>(
    (acc, item) => {
      (acc[item.category] ??= []).push(item);
      return acc;
    },
    { [ModuleCategory.Base]: [], [ModuleCategory.Premium]: [] }
  );
}
